/**
 * Las columnas del Excel/CSV del historial de pagos de la suscripción.
 *
 * Viven aparte de la pantalla —igual que en Preparación de envíos— porque son
 * datos y no JSX: así se testean generando el archivo de verdad sin montar el
 * componente.
 */

import type { ExportColumn } from "@/lib/export/types";
import type { Bill } from "@/schemas/billing";
import { formatDecimalString } from "@/utils/formatCurrency";
import { formatDate } from "@/utils/formatDate";

/**
 * `dd/MM/yyyy`, o un guión cuando no hay fecha.
 *
 * Vive acá —y no en cada componente— por la misma razón que
 * `precioDelPlan`: la usan la tarjeta de estado, la tabla del historial y
 * la ficha del plan, y un `null` impreso como "null" es un bug que hay que
 * arreglar en un solo lugar.
 */
export const fecha = (valor?: string | null): string =>
  valor ? formatDate(valor, "dd/MM/yyyy") : "—";

/**
 * Qué dice el estado de un pago, en el idioma del dueño.
 *
 * `open` se parte en dos: un pendiente que todavía no venció y uno que sí no se
 * leen igual, y es la diferencia entre "ya lo voy a pagar" y "estoy en falta".
 * El backend no manda esa distinción como estado, la manda como `overdue`.
 *
 * `under_review` es una transferencia que el dueño reportó y que **nadie revisó
 * todavía**: no está abierta ni pagada. Antes caía en el `else` y se mostraba
 * como "Vencido" — un cartel rojo que invita a pagar de nuevo algo que ya se
 * pagó.
 */
export function etiquetaDeEstado(bill: Bill): string {
  if (bill.status === "paid") return "Pagado";
  if (bill.status === "void") return "Anulado";
  if (bill.status === "under_review") return "En revisión";
  if (bill.status === "open") return bill.overdue ? "Vencido" : "Pendiente";
  // Un estado que todavía no conocemos: se muestra como viene. Meterlo en
  // "Vencido" sería afirmar algo sobre la plata del dueño que no sabemos.
  return bill.status;
}

/**
 * Por dónde entró el pago. `paid_via` viene `null` mientras no esté pagado, y
 * ahí la respuesta correcta es un guión y no "automático".
 *
 * El vocabulario es de octopus (`business/subscription/domain/bill/bill.go`):
 *
 * - `automatic` — el motor cobró con la tarjeta guardada.
 * - `checkout` — el dueño pagó desde la app. **Se llamaba `manual`**; octopus lo
 *   renombró porque chocaba con el *modo* de cobro y sigue aceptando el viejo al
 *   hidratar, así que las dos formas se contemplan.
 * - `transfer` — transferencia que un staff aprobó.
 * - `external` — la plata entró por fuera y alguien la asentó.
 *
 * Un valor desconocido se muestra **como viene**: un guión diría "no se pagó"
 * sobre una factura que sí se pagó, que es peor que un texto raro.
 */
export function comoSePago(bill: Bill): string {
  if (bill.status !== "paid") return "—";
  switch (bill.paid_via) {
    case "automatic":
      return "Automático";
    case "checkout":
    case "manual":
      return "Desde la app";
    case "transfer":
      return "Transferencia";
    case "external":
      return "Asentado a mano";
    default:
      return bill.paid_via || "—";
  }
}

export const COLUMNAS_DE_PAGOS: ExportColumn<Bill>[] = [
  {
    header: "Desde",
    accessor: (b) => b.period_start ?? null,
    type: "date",
    width: 14,
  },
  {
    header: "Hasta",
    accessor: (b) => b.period_end ?? null,
    type: "date",
    width: 14,
  },
  // `amount` es un STRING decimal: se convierte a número sólo acá, para que la
  // celda del Excel sea numérica y se pueda sumar. En pantalla va por
  // `formatCurrency`, que lo lee como string sin perder centavos.
  {
    header: "Monto",
    accessor: (b) => Number(b.amount),
    type: "currency",
    width: 16,
  },
  { header: "Estado", accessor: etiquetaDeEstado, width: 14 },
  {
    header: "Vencimiento",
    accessor: (b) => b.due_at ?? null,
    type: "date",
    width: 14,
  },
  {
    header: "Pagado el",
    accessor: (b) => b.paid_at ?? null,
    type: "date",
    width: 14,
  },
  { header: "Cómo se pagó", accessor: comoSePago, width: 16 },
  { header: "Referencia", accessor: (b) => b.provider_ref ?? "", width: 20 },
];

/**
 * "$ 25.000,00 cada 30 días" — el precio del plan con su intervalo.
 *
 * Se usa dos veces en la pantalla: como subtítulo cuando el plan tiene nombre, y
 * como TÍTULO cuando no lo tiene. Vive acá para no escribirlo dos veces y para
 * poder testear el armado sin montar el componente.
 */
export function precioDelPlan(plan: {
  price?: string | null;
  interval_days?: number | null;
}): string {
  if (!plan?.price) return "—";
  const precio = formatDecimalString(plan.price);
  return plan.interval_days
    ? `${precio} cada ${plan.interval_days} días`
    : precio;
}
